/**
 * Veda Agro — shared client-side print utility for Sales Order / Purchase Order.
 *
 * No backend/PDF service. Opens a new window, writes a self-contained A4-styled
 * HTML document, and triggers window.print() in that window. Closes the window
 * after the print dialog is dismissed (afterprint).
 *
 * Deploy target: frontend/src/pages/veda-agro/shared/printOrder.ts
 */

import { brandNameNow } from '@/lib/brand';

export interface OrderPrintAddress {
  address?: string;
  state?: string;
  city?: string;
  subDistrict?: string;
  village?: string;
  pincode?: string;
  country?: string;
}

export interface OrderPrintItem {
  product: string;
  sku?: string;
  uom?: string;
  qty: number;
  unitPrice: number;
  taxPct?: number;
}

export interface OrderPrintDoc {
  kind: 'Sales Order' | 'Purchase Order';
  number: string; // formal number ?? placeholder
  date?: string;
  deliveryDate?: string;
  company?: { name?: string; gstin?: string; stateCode?: string } | null;
  partyLabel: string; // 'Customer' | 'Vendor'
  partyName?: string;
  partyGstin?: string;
  partyPan?: string;
  billLabel: string; // SO: 'Bill To'; PO: 'Bill From'
  billTo?: OrderPrintAddress;
  shipLabel: string; // SO: 'Ship To'; PO: 'Ship From'
  shipTo?: OrderPrintAddress;
  items: OrderPrintItem[];
  incoterm?: string;
  paymentTerms?: string;
  packingSpec?: string;
  notes?: string;
  reference?: string;
  currency?: string; // 'INR'
}

/* ------------------------------------------------------------------ */
/*  Formatting helpers                                                 */
/* ------------------------------------------------------------------ */

function escapeHtml(input: string): string {
  return input
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function formatMoney(value: number, currency = 'INR'): string {
  const n = Number.isFinite(value) ? value : 0;
  const formatted = n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const symbol = currency.startsWith('INR') || currency === '' || currency === undefined ? '₹' : `${currency} `;
  return `${symbol}${formatted}`;
}

function formatDateDisplay(iso?: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return escapeHtml(iso);
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

// Formats an address block, skipping empty parts. Returns '—' if nothing present.
function formatAddress(addr?: OrderPrintAddress): string {
  if (!addr) return '—';
  const lines: string[] = [];
  if (addr.address?.trim()) lines.push(escapeHtml(addr.address.trim()));
  const localityParts = [addr.village, addr.subDistrict, addr.city]
    .filter(p => p && p.trim())
    .map(p => escapeHtml((p as string).trim()));
  if (localityParts.length) lines.push(localityParts.join(', '));
  const regionParts = [addr.state, addr.pincode]
    .filter(p => p && p.trim())
    .map(p => escapeHtml((p as string).trim()));
  if (regionParts.length) lines.push(regionParts.join(' — '));
  if (addr.country?.trim()) lines.push(escapeHtml(addr.country.trim()));
  return lines.length ? lines.join('<br/>') : '—';
}

function lineTotal(item: OrderPrintItem): number {
  // Ex-GST taxable value. Must equal qty * unitPrice so that the sum of
  // per-line "Line Total" values reconciles with the Subtotal row below
  // (GST is applied once, in the Subtotal -> GST/Tax -> Total rollup —
  // not baked into the line total itself).
  return item.qty * item.unitPrice;
}

/* ------------------------------------------------------------------ */
/*  Document builder                                                   */
/* ------------------------------------------------------------------ */

function buildHtml(doc: OrderPrintDoc): string {
  const currency = doc.currency || 'INR';
  const subtotal = doc.items.reduce((s, it) => s + it.qty * it.unitPrice, 0);
  const gstTotal = doc.items.reduce((s, it) => s + (it.qty * it.unitPrice * (it.taxPct ?? 0)) / 100, 0);
  const grandTotal = subtotal + gstTotal;

  const itemRows = doc.items
    .map((it, idx) => `
      <tr>
        <td class="num">${idx + 1}</td>
        <td>${escapeHtml(it.product || '—')}</td>
        <td>${escapeHtml(it.sku || '—')}</td>
        <td class="center">${escapeHtml(it.uom || '—')}</td>
        <td class="num">${it.qty}</td>
        <td class="num">${formatMoney(it.unitPrice, currency)}</td>
        <td class="num">${it.taxPct != null ? `${it.taxPct}%` : '—'}</td>
        <td class="num strong">${formatMoney(lineTotal(it), currency)}</td>
      </tr>`)
    .join('');

  const companyName = doc.company?.name ? escapeHtml(doc.company.name) : escapeHtml(brandNameNow());
  const companyGstin = doc.company?.gstin ? escapeHtml(doc.company.gstin) : null;
  const companyStateCode = doc.company?.stateCode ? escapeHtml(doc.company.stateCode) : null;

  const footerBits: string[] = [];
  if (doc.incoterm) footerBits.push(`<div><span class="footer-label">Incoterm</span><span>${escapeHtml(doc.incoterm)}</span></div>`);
  if (doc.paymentTerms) footerBits.push(`<div><span class="footer-label">Payment Terms</span><span>${escapeHtml(doc.paymentTerms)}</span></div>`);
  if (doc.packingSpec) footerBits.push(`<div><span class="footer-label">Packing Spec</span><span>${escapeHtml(doc.packingSpec)}</span></div>`);
  if (doc.reference) footerBits.push(`<div><span class="footer-label">Reference</span><span>${escapeHtml(doc.reference)}</span></div>`);

  const notesBlock = doc.notes
    ? `<div class="notes"><span class="footer-label">Notes</span><p>${escapeHtml(doc.notes)}</p></div>`
    : '';

  return `<!doctype html>
<html>
<head>
<meta charset="utf-8" />
<title>${escapeHtml(doc.kind)} ${escapeHtml(doc.number)}</title>
<style>
  * { box-sizing: border-box; }
  html, body {
    margin: 0;
    padding: 0;
    background: #fff;
    color: #111;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
    font-size: 12px;
    line-height: 1.45;
  }
  .sheet {
    width: 210mm;
    min-height: 297mm;
    margin: 0 auto;
    padding: 14mm 12mm;
  }
  .header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    border-bottom: 2px solid #111;
    padding-bottom: 10px;
    margin-bottom: 16px;
  }
  .header h1 {
    margin: 0 0 2px 0;
    font-size: 20px;
    letter-spacing: 0.02em;
  }
  .header .meta { font-size: 11px; color: #444; }
  .header .doc-no { text-align: right; }
  .header .doc-no .num { font-size: 15px; font-weight: 700; }
  .header .doc-no .dates { margin-top: 4px; font-size: 11px; color: #444; }
  .parties {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
    margin-bottom: 16px;
  }
  .party-box {
    border: 1px solid #ccc;
    border-radius: 4px;
    padding: 10px 12px;
  }
  .party-box h3 {
    margin: 0 0 6px 0;
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: #666;
  }
  .party-box .name { font-size: 13px; font-weight: 700; margin-bottom: 2px; }
  .party-box .idline { font-size: 11px; color: #444; margin-bottom: 6px; }
  .addr-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
    margin-top: 8px;
  }
  .addr-grid .addr-block { font-size: 11px; }
  .addr-grid .addr-label {
    display: block;
    font-size: 9px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #888;
    margin-bottom: 2px;
  }
  table.items {
    width: 100%;
    border-collapse: collapse;
    margin-bottom: 14px;
  }
  table.items th, table.items td {
    border: 1px solid #ccc;
    padding: 6px 8px;
    font-size: 11px;
  }
  table.items thead th {
    background: #f2f2f2;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    font-size: 9.5px;
    color: #333;
    text-align: left;
  }
  table.items td.num { text-align: right; font-variant-numeric: tabular-nums; }
  table.items th.num { text-align: right; }
  table.items td.center { text-align: center; }
  table.items td.strong { font-weight: 700; }
  .totals {
    margin-left: auto;
    width: 260px;
    margin-bottom: 20px;
  }
  .totals .row {
    display: flex;
    justify-content: space-between;
    padding: 3px 0;
    font-size: 11.5px;
  }
  .totals .row.grand {
    border-top: 2px solid #111;
    margin-top: 4px;
    padding-top: 6px;
    font-size: 14px;
    font-weight: 700;
  }
  .footer {
    border-top: 1px solid #ccc;
    padding-top: 10px;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 6px 20px;
    font-size: 11px;
  }
  .footer .footer-label {
    display: inline-block;
    min-width: 96px;
    color: #666;
    text-transform: uppercase;
    font-size: 9px;
    letter-spacing: 0.04em;
    vertical-align: top;
  }
  .notes {
    grid-column: 1 / -1;
    margin-top: 4px;
  }
  .notes p { margin: 2px 0 0 0; white-space: pre-wrap; }
  @media print {
    .sheet { width: auto; min-height: 0; margin: 0; padding: 8mm 10mm; }
    @page { size: A4; margin: 10mm; }
  }
</style>
</head>
<body>
  <div class="sheet">
    <div class="header">
      <div>
        <h1>${companyName}</h1>
        <div class="meta">
          ${companyGstin ? `GSTIN: ${companyGstin}` : ''}${companyGstin && companyStateCode ? ' &nbsp;·&nbsp; ' : ''}${companyStateCode ? `State Code: ${companyStateCode}` : ''}
        </div>
      </div>
      <div class="doc-no">
        <div class="meta">${escapeHtml(doc.kind)}</div>
        <div class="num">${escapeHtml(doc.number)}</div>
        <div class="dates">
          Date: ${formatDateDisplay(doc.date)}<br/>
          Delivery: ${formatDateDisplay(doc.deliveryDate)}
        </div>
      </div>
    </div>

    <div class="parties">
      <div class="party-box">
        <h3>${escapeHtml(doc.partyLabel)}</h3>
        <div class="name">${doc.partyName ? escapeHtml(doc.partyName) : '—'}</div>
        <div class="idline">
          ${doc.partyGstin ? `GSTIN: ${escapeHtml(doc.partyGstin)}` : ''}${doc.partyGstin && doc.partyPan ? ' &nbsp;·&nbsp; ' : ''}${doc.partyPan ? `PAN: ${escapeHtml(doc.partyPan)}` : ''}
        </div>
        <div class="addr-grid">
          <div class="addr-block">
            <span class="addr-label">${escapeHtml(doc.billLabel)}</span>
            ${formatAddress(doc.billTo)}
          </div>
          <div class="addr-block">
            <span class="addr-label">${escapeHtml(doc.shipLabel)}</span>
            ${formatAddress(doc.shipTo)}
          </div>
        </div>
      </div>
      <div class="party-box">
        <h3>${escapeHtml(doc.kind)} Summary</h3>
        <div class="idline">Currency: ${escapeHtml(currency)}</div>
        <div class="idline">Lines: ${doc.items.length}</div>
      </div>
    </div>

    <table class="items">
      <thead>
        <tr>
          <th class="num">#</th>
          <th>Product</th>
          <th>SKU</th>
          <th class="center">UOM</th>
          <th class="num">Qty</th>
          <th class="num">Unit Price</th>
          <th class="num">Tax %</th>
          <th class="num">Line Total</th>
        </tr>
      </thead>
      <tbody>
        ${itemRows}
      </tbody>
    </table>

    <div class="totals">
      <div class="row"><span>Subtotal</span><span>${formatMoney(subtotal, currency)}</span></div>
      <div class="row"><span>GST / Tax</span><span>+${formatMoney(gstTotal, currency)}</span></div>
      <div class="row grand"><span>Total</span><span>${formatMoney(grandTotal, currency)}</span></div>
    </div>

    <div class="footer">
      ${footerBits.join('\n      ')}
      ${notesBlock}
    </div>
  </div>
</body>
</html>`;
}

/* ------------------------------------------------------------------ */
/*  Public API                                                         */
/* ------------------------------------------------------------------ */

export function printOrder(doc: OrderPrintDoc): void {
  const html = buildHtml(doc);
  const printWindow = window.open('', '_blank', 'width=900,height=1000');
  if (!printWindow) {
    // eslint-disable-next-line no-alert
    alert('Please allow popups for this site to print the order.');
    return;
  }
  printWindow.document.open();
  printWindow.document.write(html);
  printWindow.document.close();

  const triggerPrint = () => {
    printWindow.focus();
    printWindow.print();
  };

  printWindow.addEventListener('afterprint', () => {
    printWindow.close();
  });

  // Give the new window a tick to lay out before invoking print().
  if (printWindow.document.readyState === 'complete') {
    triggerPrint();
  } else {
    printWindow.addEventListener('load', triggerPrint);
    // Fallback in case 'load' doesn't fire reliably in some browsers.
    setTimeout(triggerPrint, 300);
  }
}
